import { useEffect, useMemo, useRef, useState } from "react";
import Reveal from "@/components/Reveal";
import { Building2, Shield, Users2, Crown, Sparkles, Map as MapIcon, Zap, AlertTriangle, BookOpen, ServerCog } from "lucide-react";
import { useSiteContent } from "@/admin/SiteContent";

const LORE_ICON_MAP = {
  Building2,
  Shield,
  Users2,
  Crown,
  Sparkles,
  Map: MapIcon,
  Zap,
  AlertTriangle,
  BookOpen,
  ServerCog,
};

const FALLBACK_LORE = {
  kicker: "Lore Ufficiale · Vol. I",
  title_lead: "L'Illusione",
  title_accent: "d'Oro",
  intro_p1:
    "Guardala da Vinewood Hills, di notte. Sembra perfetta. Una griglia geometrica di luci dorate e neon celesti che taglia il buio del Pacifico.",
  intro_p2: "Ma la verità è che a Los Santos la purezza è solo una vernice molto costosa.",
  outro_kicker: "Fine del Volume",
  outro_body: "Cosa sei disposto a perdere per lasciare il tuo segno?",
  chapters: [],
};

export const Lore = () => {
  const { content } = useSiteContent();
  const sectionRef = useRef(null);
  const [t, setT] = useState(0);

  const data = useMemo(() => {
    const raw = (content && content.lore) || {};
    return {
      ...FALLBACK_LORE,
      ...raw,
      chapters:
        Array.isArray(raw.chapters) && raw.chapters.length > 0
          ? raw.chapters
          : FALLBACK_LORE.chapters,
    };
  }, [content]);

  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const center = window.innerHeight / 2;
      const distance = r.top - center;
      setT(Math.max(-300, Math.min(300, -distance * 0.18)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="lore"
      ref={sectionRef}
      data-testid="lore-root"
      className="relative py-28 sm:py-40 overflow-hidden"
    >
      {/* Cinematic background glows + parallax */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 600px at 20% 20%, rgba(120, 15, 15, 0.18), transparent 60%), radial-gradient(700px 500px at 85% 60%, rgba(212, 175, 55, 0.10), transparent 70%), radial-gradient(800px 500px at 50% 95%, rgba(120, 15, 15, 0.10), transparent 70%)",
          transform: `translate3d(0, ${t * 0.4}px, 0)`,
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle, #D4AF37 1px, transparent 1.4px)",
          backgroundSize: "70px 70px",
          transform: `translate3d(0, ${t * 0.2}px, 0)`,
        }}
      />

      <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
        {/* HERO — Title */}
        <div className="text-center mb-24 sm:mb-32">
          <Reveal>
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#D4AF37]/60" />
              <span className="font-mono-pl text-[10px] uppercase tracking-[0.42em] text-[#D4AF37]">
                {data.kicker}
              </span>
              <span className="h-px w-12 bg-[#D4AF37]/60" />
            </div>
          </Reveal>
          <Reveal delay={1}>
            <h2 className="font-display text-[clamp(2.8rem,9vw,8rem)] uppercase text-[#F5F5F5] leading-[0.9] tracking-wide glow-gold-strong">
              {data.title_lead}
              <br />
              <span className="text-gold-bright glow-gold-strong">{data.title_accent}</span>
            </h2>
          </Reveal>
          <Reveal delay={3}>
            <div className="mt-12 max-w-3xl mx-auto text-left sm:text-center space-y-4 text-[#F5F5F5]/85 text-base sm:text-lg leading-relaxed">
              {String(data.intro_p1 || "")
                .split(/\n\n+/)
                .map((para, k) => (
                  <p key={k}>{para}</p>
                ))}
              <p className="font-display uppercase text-lg sm:text-2xl tracking-wide text-[#F5F5F5] pt-3">
                {data.intro_p2}
              </p>
            </div>
          </Reveal>
        </div>

        {/* TIMELINE — Vertical chapters */}
        <div className="relative">
          {/* Vertical gold line */}
          <div
            aria-hidden="true"
            className="hidden sm:block absolute top-2 bottom-2 left-[18px] sm:left-1/2 sm:-translate-x-1/2 w-px"
            style={{
              background:
                "linear-gradient(180deg, transparent 0%, rgba(212,175,55,0.6) 12%, rgba(212,175,55,0.4) 50%, rgba(212,175,55,0.6) 88%, transparent 100%)",
            }}
          />

          <div className="space-y-20 sm:space-y-28">
            {data.chapters.map((c, i) => {
              const Icon = LORE_ICON_MAP[c.icon] || Building2;
              const left = i % 2 === 0;
              return (
                <Reveal key={c.n || i} delay={1}>
                  <div className="relative grid sm:grid-cols-2 sm:gap-12 items-center">
                    {/* Marker */}
                    <div
                      aria-hidden="true"
                      className="absolute left-[10px] sm:left-1/2 sm:-translate-x-1/2 top-1 z-10 w-5 h-5 rounded-full flex items-center justify-center"
                      style={{
                        background:
                          "radial-gradient(circle, #D4AF37 0%, rgba(212,175,55,0.2) 65%, transparent 100%)",
                        boxShadow:
                          "0 0 16px rgba(212,175,55,0.7), 0 0 36px rgba(212,175,55,0.45)",
                      }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0B0B0F]" />
                    </div>

                    {/* Content card — alternating side on desktop */}
                    <div
                      className={`pl-12 sm:pl-0 ${
                        left ? "sm:pr-16 sm:text-right" : "sm:col-start-2 sm:pl-16"
                      }`}
                    >
                      <div
                        className={`flex items-center gap-3 mb-4 ${
                          left ? "sm:justify-end" : ""
                        }`}
                      >
                        <span className="w-10 h-10 rounded-xl bg-[#0B0B0F]/70 border border-[#D4AF37]/30 flex items-center justify-center">
                          <Icon size={16} className="text-[#D4AF37]" strokeWidth={1.7} />
                        </span>
                        <span className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
                          Capitolo {c.n}
                        </span>
                      </div>
                      <h3 className="font-display text-3xl sm:text-5xl uppercase text-[#F5F5F5] leading-[0.98] tracking-wide">
                        {c.title}
                      </h3>
                      {c.subtitle && (
                        <p
                          className={`mt-2 font-mono-pl text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]/85 italic ${
                            left ? "sm:text-right" : ""
                          }`}
                        >
                          {c.subtitle}
                        </p>
                      )}
                      <div
                        className={`mt-5 text-[#B6B6BE] text-base sm:text-lg leading-relaxed max-w-md space-y-4 ${
                          left ? "sm:ml-auto" : ""
                        }`}
                      >
                        {String(c.body || "")
                          .split(/\n\n+/)
                          .map((para, k) => (
                            <p key={k}>{para}</p>
                          ))}
                      </div>
                    </div>

                    {/* Roman numeral side */}
                    <div
                      className={`hidden sm:flex items-center ${
                        left
                          ? "sm:col-start-2 sm:justify-start sm:pl-16"
                          : "sm:col-start-1 sm:row-start-1 sm:justify-end sm:pr-16"
                      }`}
                    >
                      <div className="relative">
                        <span
                          className="font-display text-[10rem] lg:text-[14rem] leading-none uppercase select-none"
                          style={{
                            color: "transparent",
                            WebkitTextStroke: "1px rgba(212, 175, 55, 0.45)",
                          }}
                        >
                          {c.n}
                        </span>
                        <span
                          className="absolute inset-0 font-display text-[10rem] lg:text-[14rem] leading-none uppercase text-[#D4AF37]/10 pointer-events-none"
                        >
                          {c.n}
                        </span>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Final epigraph */}
        <Reveal delay={2} className="mt-28 text-center">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-[#D4AF37]/60" />
            <span className="font-mono-pl text-[10px] uppercase tracking-[0.42em] text-[#D4AF37]">
              {data.outro_kicker}
            </span>
            <span className="h-px w-12 bg-[#D4AF37]/60" />
          </div>
          <p className="font-display text-2xl sm:text-3xl uppercase text-[#F5F5F5]/80 tracking-wide max-w-2xl mx-auto leading-snug">
            {data.outro_body}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Lore;
